import { useEffect, useRef, type RefObject } from 'react';

import { focusInitial, trapTab } from './dom';

export interface OverlayOptions {
  open: boolean;
  onClose: () => void;
  /** Keep Tab inside the overlay. False for a non-modal side panel. */
  trap: boolean;
  /** Focus this instead of the first focusable child. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Prevent the page behind from scrolling while open. */
  lockScroll?: boolean;
}

/**
 * The four things every overlay must get right: move focus in, keep Tab inside,
 * close on Escape, and put focus back where it was. Shared by Modal and Drawer
 * so the two cannot drift.
 */
export const useOverlay = ({
  open,
  onClose,
  trap,
  initialFocus,
  lockScroll = true,
}: OverlayOptions): RefObject<HTMLDivElement | null> => {
  const ref = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const node = ref.current;
    if (node !== null) focusInitial(node, initialFocus?.current ?? null);

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      const current = ref.current;
      if (trap && current !== null) trapTab(current, event);
    };

    // Capture phase: the trap must win before a control inside decides Tab
    // means something else.
    document.addEventListener('keydown', onKeyDown, true);

    const previousOverflow = document.body.style.overflow;
    if (lockScroll) document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (lockScroll) document.body.style.overflow = previousOverflow;
      // Restoring focus is what makes an overlay usable from the keyboard: the
      // user comes back to the control they opened it from.
      previous?.focus();
    };
    // `initialFocus` is a ref object and `onClose` is read through a ref, so the
    // effect deliberately runs only on open/close.
  }, [open, trap, lockScroll]);

  return ref;
};
